import type { EffectiveTier } from './effectiveTier';
import type { TokenBalance } from '~/lib/.server/rateLimit';
import type { TokenStats } from '~/lib/.server/rateLimit';

export function computeReferralTokensAggregate(tokenStats: TokenStats | undefined, tier: EffectiveTier | undefined) {
  if (!tokenStats || !tokenStats.referralTokens) {
    return undefined;
  }

  let available = tokenStats.referralTokens.free.available;

  if (tier?.plan === 'pro') {
    available += tokenStats.referralTokens.paid.available;
  }

  let used = tokenStats.referralTokens.free.used;

  if (tier?.plan === 'pro') {
    used += tokenStats.referralTokens.paid.used;
  }

  return { available, used };
}

export function computeMonthlyRecurringTokensLeft(tokenStats: TokenStats | undefined): number {
  if (!tokenStats) {
    return 0;
  }

  const { totalThisMonth, maxPerMonth } = tokenStats;

  return Math.max(maxPerMonth - totalThisMonth, 0);
}

export function computeTokensLeft(
  tokenStats: TokenStats | undefined,
  tier: EffectiveTier | undefined,
  period?: 'daily' | 'monthly',
) {
  if (!tokenStats || !tokenStats.purchased) {
    return {};
  }

  const { referralTokens } = tokenStats;

  let referralTokensLeft = 0;

  if (referralTokens?.free) {
    referralTokensLeft += computeTokensLeftForBalance(referralTokens.free);
  }

  if (referralTokens?.paid && tier?.plan === 'pro') {
    referralTokensLeft += computeTokensLeftForBalance(referralTokens.paid);
  }

  let tokenTodayLeft =
    tokenStats.maxPerDay - Math.min(tokenStats.totalToday, tokenStats.maxPerDay) + referralTokensLeft;

  let tokenThisMonthLeft = computeTokensLeftForBalance(getRegularTokensBalance(tokenStats)) + referralTokensLeft;

  if (tier?.plan !== 'personal') {
    const purchasedLeft = computeTokensLeftForBalance(tokenStats.purchased);

    tokenThisMonthLeft += purchasedLeft;
    tokenTodayLeft += purchasedLeft;
  }

  if ((tokenThisMonthLeft <= tokenTodayLeft && !period) || period === 'monthly') {
    return {
      period: 'monthly',
      closeToMax: isCloseToMax(getRegularTokensBalance(tokenStats)),
      remainingTokens: tokenThisMonthLeft,
      maxTokens: tokenStats.maxPerMonth,
    };
  }

  return {
    period: 'daily',
    closeToMax: isCloseToMax({ used: tokenStats.totalToday, available: tokenStats.maxPerDay }),
    remainingTokens: tokenTodayLeft,
    maxTokens: tokenStats.maxPerDay,
  };
}

export function computeAllTokensLeft(tokenStats: TokenStats | undefined, tier: EffectiveTier | undefined): number {
  if (!tokenStats) {
    return 0;
  }

  // compute referral + regular + purchased tokens left
  const { remainingTokens } = computeTokensLeft(tokenStats, tier, 'monthly');
  const specialTokensLeft = computeTokensLeftForBalance(tokenStats.specialTokens);
  const tokensLeft = (remainingTokens || 0) + specialTokensLeft;

  return tokensLeft;
}

export function getRegularTokensBalance(tokenStats: TokenStats): TokenBalance {
  if (!tokenStats.regularTokens) {
    return { available: tokenStats.maxPerMonth, used: tokenStats.totalThisMonth };
  }

  return tokenStats.regularTokens;
}

export function isCloseToMax(stats: TokenBalance) {
  return (stats.available - stats.used) / stats.available < 0.33;
}

export function computeTokensLeftForBalance(stats: TokenBalance | undefined) {
  // the min is not strictly needed but to make sure in case of error we don't show a negative number
  return stats ? stats.available - Math.min(stats.used, stats.available) : 0;
}
